import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import DashboardLayout from './DashboardLayout';
import ConfirmationModal from './ConfirmationModal';
import { adminApi } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useSuccessAlert, useErrorAlert, useWarningAlert } from './AlertSystem';
import { extractErrorMessage } from '../lib/utils';
import './AdminUsersManagement.css';

interface User {
  id: string;
  name: string;
  email: string;
  role: {
    id: string;
    name: string;
    permissions: Record<string, any>;
  } | null;
  email_verified: boolean;
  last_login: string | null;
  created_at: string;
  profile_picture?: string;
}

interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  isEditable: boolean;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface CreateUserData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: string;
}

interface EditUserData {
  name: string;
  email: string;
  role: string;
  email_verified: boolean;
}

const AdminUsersManagement: React.FC = () => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [editUserData, setEditUserData] = useState<EditUserData>({
    name: '',
    email: '',
    role: 'USER',
    email_verified: true
  });
  const [createUserData, setCreateUserData] = useState<CreateUserData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'USER'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Alert hooks
  const showSuccess = useSuccessAlert();
  const showError = useErrorAlert();
  const showWarning = useWarningAlert();
  
  // Ref for search input to maintain focus
  const searchInputRef = useRef<HTMLInputElement>(null);
  const wasSearchingRef = useRef(false);
  
  // Enhanced search input handler that preserves focus
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    
    // Ensure the input stays focused during typing
    if (searchInputRef.current) {
      // Store cursor position
      const cursorPosition = searchInputRef.current.selectionStart;
      
      // Use setTimeout to restore focus after any potential re-renders
      setTimeout(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
          // Restore cursor position
          if (cursorPosition !== null) {
            searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
          }
        }
      }, 1);
    }
  };

  const usersPerPage = 10;

  // Load roles function
  const loadRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await adminApi.getRoles();
      
      if (response.success && response.data) {
        setRoles(response.data);
      } else {
        console.error('Failed to load roles:', response);
        const errorMsg = extractErrorMessage(response);
        showError(errorMsg, 'Error Loading Roles');
      }
    } catch (err) {
      console.error('Load roles error:', err);
      showError('Failed to connect to server', 'Connection Error');
    } finally {
      setRolesLoading(false);
    }
  };

  // Single function to load users - NO USEEFFECT DEPENDENCIES 
  const loadUsers = async () => {
    try {
      // Show loading only for initial load, use tableLoading for searches
      if (users.length === 0) {
        setLoading(true);
      } else {
        setTableLoading(true);
      }
      
      const response = await adminApi.getUsers({
        page: currentPage,
        limit: usersPerPage,
        search: debouncedSearch.trim() || undefined,
        role: roleFilter !== 'all' ? roleFilter : undefined
      });

      if (response.success && response.data) {
        setUsers(response.data.users || []);
        setPagination(response.data.pagination);
      } else {
        const errorMsg = extractErrorMessage(response);
        
        // Handle authentication errors
        if (response.data?.redirect === '/login') {
          window.location.href = '/login';
          return;
        }
        
        // Only show error alert for non-rate-limited errors
        if (!response.message?.includes('Too many')) {
          showError(errorMsg, 'Error Loading Users');
        }
        
        console.error('Fetch users error:', errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Failed to connect to server';
      showError(errorMsg, 'Connection Error');
      console.error('Fetch users error:', err);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  // Refetch function for CRUD operations
  const refetchUsers = loadUsers;

  // ONLY ONE USEEFFECT - Initial load only
  useEffect(() => {
    loadUsers();
    loadRoles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependencies - only runs once

  // Debounce search input - updates debouncedSearch after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300); // Short debounce for search input

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Manual trigger when debouncedSearch or roleFilter changes
  useEffect(() => {
    if (currentPage === 1) {
      loadUsers(); // Reload with current filters
    } else {
      setCurrentPage(1); // This will trigger loadUsers via page effect
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, roleFilter]);

  // Manual trigger when page changes
  useEffect(() => {
    if (currentPage > 1) { // Don't run on initial mount
      loadUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Focus management for search input during transitions
  useEffect(() => {
    // Store if user was searching when results become empty
    if (users.length === 0 && debouncedSearch.length > 0) {
      wasSearchingRef.current = true;
    }
    
    // Restore focus if user was searching and results come back
    if (wasSearchingRef.current && users.length > 0 && debouncedSearch.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 10);
      wasSearchingRef.current = false;
    }
    
    // Reset flag if search is cleared
    if (debouncedSearch.length === 0) {
      wasSearchingRef.current = false;
    }
  }, [users.length, debouncedSearch]);

  // Additional focus preservation for input changes
  useEffect(() => {
    // Preserve focus during any re-renders if user is actively typing
    const preserveFocus = () => {
      if (searchQuery.length > 0 && searchInputRef.current && 
          document.activeElement !== searchInputRef.current && 
          !document.activeElement?.closest('.custom-select')) {
        // Only restore focus if user isn't interacting with the select dropdown
        searchInputRef.current.focus();
      }
    };
    
    // Small delay to handle re-renders
    const timer = setTimeout(preserveFocus, 50);
    return () => clearTimeout(timer);
  }, [users, searchQuery]); // Re-run when users change

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    // Ensure we're displaying in the user's local timezone
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatLastLogin = (dateString: string | null) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    
    // Calculate difference in UTC to avoid timezone issues
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return formatDate(dateString);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Super Admin': return '#ef4444'; // Red for Super Admin
      case 'Admin': return '#8b5cf6';       // Purple for Admin
      case 'User': return '#3b82f6';        // Blue for User
      default: return '#6b7280';
    }
  };

  const getVerificationStatus = (verified: boolean) => {
    return verified ? 'Verified' : 'Unverified';
  };

  const getVerificationColor = (verified: boolean) => {
    return verified ? '#10b981' : '#f59e0b';
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditUserData({
      name: user.name,
      email: user.email,
      role: user.role?.id || '',
      email_verified: Boolean(user.email_verified)
    });
    setIsEditModalOpen(true);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    const defaultRole = roles.find(role => role.name === 'User') || roles[0];
    setCreateUserData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: defaultRole?.id || ''
    });
    setIsCreateModalOpen(true);
  };

  const handleDeleteUser = async (user: User) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setIsSubmitting(true);
      const response = await adminApi.deleteUser(userToDelete.id);
      
      if (response.success) {
        await refetchUsers();
        showSuccess(`${userToDelete.name} has been deleted successfully`, 'User Deleted');
      } else {
        showError(response.error || 'Failed to delete user', 'Delete Failed');
      }
    } catch (err) {
      console.error('Delete user error:', err);
      showError('Network error occurred while deleting user', 'Delete Failed');
    } finally {
      setIsSubmitting(false);
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    // Validation
    if (!editUserData.name.trim()) {
      showWarning('Name is required', 'Validation Error');
      return;
    }

    if (!editUserData.email.trim() || !/\S+@\S+\.\S+/.test(editUserData.email)) {
      showWarning('Valid email is required', 'Validation Error');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Create a fresh copy of the data to ensure we have the latest state
      const dataToSend = {
        name: editUserData.name,
        email: editUserData.email,
        role: editUserData.role,
        email_verified: editUserData.email_verified
      };

      const response = await adminApi.updateUser(selectedUser.id, dataToSend);
      
      if (response.success) {
        await refetchUsers();
        setIsEditModalOpen(false);
        showSuccess(`${editUserData.name} has been updated successfully`, 'User Updated');
      } else {
        const errorMsg = extractErrorMessage(response);
        showError(errorMsg, 'Update Failed');
      }
    } catch (err) {
      console.error('Update user error:', err);
      showError('Network error occurred while updating user', 'Update Failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateNewUser = async () => {
    // Validation
    if (!createUserData.name.trim()) {
      showWarning('Name is required', 'Validation Error');
      return;
    }

    if (!createUserData.email.trim() || !/\S+@\S+\.\S+/.test(createUserData.email)) {
      showWarning('Valid email is required', 'Validation Error');
      return;
    }

    if (!createUserData.password || createUserData.password.length < 8) {
      showWarning('Password must be at least 8 characters long', 'Validation Error');
      return;
    }

    if (createUserData.password !== createUserData.confirmPassword) {
      showWarning('Passwords do not match', 'Validation Error');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await adminApi.createUser({
        name: createUserData.name,
        email: createUserData.email,
        password: createUserData.password,
        role: createUserData.role
      });
      
      if (response.success) {
        await refetchUsers();
        setIsCreateModalOpen(false);
        showSuccess(`${createUserData.name} has been created successfully`, 'User Created');
      } else {
        const errorMsg = extractErrorMessage(response);
        showError(errorMsg, 'Creation Failed');
      }
    } catch (err) {
      console.error('Create user error:', err);
      showError('Network error occurred while creating user', 'Creation Failed');
    } finally {
      setIsSubmitting(false);
    }
  };



    


  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    ...roles.map(role => ({ value: role.id, label: role.name }))
  ];

  const userRoleOptions = roles.map(role => ({ 
    value: role.id, 
    label: role.name 
  }));

  // Custom styles for react-select
  const selectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      background: 'rgba(26, 26, 39, 0.8)',
      border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(148, 163, 184, 0.2)'}`,
      borderRadius: '8px',
      padding: '2px 4px',
      color: 'var(--text-primary)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif",
      minHeight: '40px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.15)' : 'none',
      cursor: 'pointer',
      '&:hover': {
        border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(99, 102, 241, 0.4)'}`
      }
    }),
    menu: (provided: any) => ({
      ...provided,
      background: 'rgba(26, 26, 39, 0.95)',
      border: '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
      zIndex: 999999999,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)'
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      background: state.isSelected 
        ? 'rgba(99, 102, 241, 0.2)' 
        : state.isFocused 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'transparent',
      color: state.isSelected ? 'var(--primary-purple)' : 'var(--text-primary)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif",
      padding: '10px 14px',
      cursor: 'pointer',
      '&:hover': {
        background: state.isSelected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.08)'
      }
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--text-primary)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif"
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'var(--text-muted)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif"
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--text-primary)',
      fontSize: '0.875rem',
      fontFamily: "'Inter', 'Chivo', sans-serif"
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: 'var(--text-muted)',
      padding: '6px',
      '&:hover': {
        color: 'var(--text-primary)'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    })
  };

  if (loading && users.length === 0) {
    return (
      <div className="admin-users-loading">
        <div className="admin-users-loading-spinner">
          <i className="ri-loader-4-line"></i>
        </div>
        <div className="admin-users-loading-text">Loading users...</div>
      </div>
    );
  }

  return (
    <DashboardLayout title="Users Management">
      <div className="admin-users">
        {/* Header */}
        <div className="admin-users-header">
          <div className="admin-users-header-content">
            <h1 className="admin-users-title">User Management</h1>
            <p className="admin-users-subtitle">
              Manage user accounts, roles, and permissions across your platform
            </p>
          </div>
          <div className="admin-users-stats">
            <div className="admin-users-stat">
              <div className="admin-users-stat-value">{pagination?.total || 0}</div>
              <div className="admin-users-stat-label">Total Users</div>
            </div>
            <div className="admin-users-stat">
              <div className="admin-users-stat-value">
                {users.filter(u => u.email_verified).length}
              </div>
              <div className="admin-users-stat-label">Verified</div>
            </div>
            <div className="admin-users-stat">
              <div className="admin-users-stat-value">
                {users.filter(u => u.role?.name === 'Admin' || u.role?.name === 'Super Admin').length}
              </div>
              <div className="admin-users-stat-label">Admins</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-users-filters">
          <div className="admin-users-search">
            <i className="ri-search-line"></i>
            <input
              key="search-input" // Stable key to prevent re-creation
              ref={searchInputRef}
              type="text"
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="admin-users-search-input"
              autoComplete="off"
            />
          </div>
          <div className="admin-users-filter-group">
            <Select
              options={roleOptions}
              value={roleOptions.find(option => option.value === roleFilter)}
              onChange={(option) => setRoleFilter(option?.value || 'all')}
              placeholder="Filter by role"
              styles={selectStyles}
              isSearchable={false}
              className="admin-users-role-filter"
              isDisabled={loading}
            />
            {(hasPermission('users_create') || isSuperAdmin()) && (
              <button 
                className="admin-users-create-btn"
                onClick={handleCreateUser}
                disabled={loading || isSubmitting}
              >
                <i className="ri-add-line"></i>
                <span>Create User</span>
              </button>
            )}
          </div>
        </div>

        {/* Loading indicator for filters */}
        {loading && users.length > 0 && (
          <div className="admin-users-loading-indicator">
            <i className="ri-loader-4-line"></i>
            <span>Updating results...</span>
          </div>
        )}

        {/* Users Table */}
        <div className="admin-users-table-container" style={{ position: 'relative' }}>
          {tableLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '0.75rem'
            }}>
              <div style={{ 
                background: 'rgba(23, 23, 28, 0.9)', 
                padding: '1rem 1.5rem', 
                borderRadius: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--text-secondary)',
                fontSize: '0.875rem'
              }}>
                <i className="ri-loader-4-line" style={{ animation: 'spin 1s linear infinite' }}></i>
                Searching...
              </div>
            </div>
          )}
          {users.length === 0 && !loading && !tableLoading ? (
            <div className="admin-users-empty">
              <i className="ri-user-line"></i>
              <h3>No users found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="admin-users-table" style={{ opacity: tableLoading ? 0.7 : 1 }}>
              <div className="admin-users-table-header">
                <div className="admin-users-table-cell">User</div>
                <div className="admin-users-table-cell">Role</div>
                <div className="admin-users-table-cell">Status</div>
                <div className="admin-users-table-cell">Last Login</div>
                <div className="admin-users-table-cell">Join Date</div>
                <div className="admin-users-table-cell">Actions</div>
              </div>
              <div className="admin-users-table-body">
                {users.map((user) => (
                  <div key={user.id} className="admin-users-table-row">
                    <div className="admin-users-table-cell">
                      <div className="admin-user-info">
                        <div className="admin-user-avatar">
                          {user.profile_picture ? (
                            <img src={user.profile_picture} alt={user.name} />
                          ) : (
                            <i className="ri-user-fill"></i>
                          )}
                        </div>
                        <div className="admin-user-details">
                          <div className="admin-user-name">{user.name}</div>
                          <div className="admin-user-email">{user.email}</div>
                        </div>
                      </div>
                    </div>
                    <div className="admin-users-table-cell">
                      <span 
                        className="admin-user-role-badge"
                        style={{ backgroundColor: `${getRoleColor(user.role?.name || 'User')}20`, color: getRoleColor(user.role?.name || 'User') }}
                      >
                        {user.role?.name || 'User'}
                      </span>
                    </div>
                    <div className="admin-users-table-cell">
                      <span 
                        className="admin-user-status-badge"
                        style={{ backgroundColor: `${getVerificationColor(user.email_verified)}20`, color: getVerificationColor(user.email_verified) }}
                      >
                        {getVerificationStatus(user.email_verified)}
                      </span>
                    </div>
                    <div className="admin-users-table-cell">
                      <span className="admin-user-last-login">
                        {formatLastLogin(user.last_login)}
                      </span>
                    </div>
                    <div className="admin-users-table-cell">
                      <span className="admin-user-join-date">
                        {formatDate(user.created_at)}
                      </span>
                    </div>
                    <div className="admin-users-table-cell">
                      <div className="admin-user-actions">
                        {(hasPermission('users_edit') || isSuperAdmin()) && (
                          <button 
                            className="admin-user-action-btn"
                            onClick={() => handleEditUser(user)}
                            title="Edit user"
                            disabled={isSubmitting}
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                        )}
                        {(hasPermission('users_delete') || isSuperAdmin()) && (
                          <button 
                            className="admin-user-action-btn admin-user-delete-btn"
                            onClick={() => handleDeleteUser(user)}
                            title="Delete user"
                            disabled={isSubmitting}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
        )}
                    </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="admin-users-pagination">
            <button 
              className="admin-users-pagination-btn"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1 || loading}
            >
              <i className="ri-arrow-left-line"></i>
              Previous
            </button>
            <div className="admin-users-pagination-info">
              Page {currentPage} of {pagination.pages} ({pagination.total} users)
            </div>
            <button 
              className="admin-users-pagination-btn"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pagination.pages || loading}
            >
              Next
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && selectedUser && (
          <div className="admin-users-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
            <div className="admin-users-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-users-modal-header">
                <h3>Edit User</h3>
                <button onClick={() => setIsEditModalOpen(false)}>
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="admin-users-modal-content">
                <div className="admin-users-form">
                  <div className="admin-users-form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={editUserData.name}
                      onChange={(e) => setEditUserData({...editUserData, name: e.target.value})}
                      placeholder="Enter full name"
                    />
                  </div>
                  <div className="admin-users-form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={editUserData.email}
                      onChange={(e) => setEditUserData({...editUserData, email: e.target.value})}
                      placeholder="Enter email address"
                    />
                  </div>
                  <div className="admin-users-form-group">
                    <label>Role</label>
                    <Select
                      options={userRoleOptions}
                      value={userRoleOptions.find(option => option.value === editUserData.role)}
                      onChange={(option) => setEditUserData({...editUserData, role: option?.value || ''})}
                      styles={selectStyles}
                      isSearchable={false}
                      placeholder="Select role"
                      isLoading={rolesLoading}
                    />
                  </div>
                  <div className="admin-users-form-group">
                    <label className="admin-users-checkbox-label">
                      <input
                        type="checkbox"
                        checked={editUserData.email_verified}
                        onChange={(e) => {
                          setEditUserData({...editUserData, email_verified: e.target.checked});
                        }}
                      />
                      <span>Email Verified</span>
                    </label>
                  </div>
                  <div className="admin-users-form-actions">
                    <button 
                      className="admin-users-btn admin-users-btn-secondary"
                      onClick={() => setIsEditModalOpen(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="admin-users-btn admin-users-btn-primary"
                      onClick={handleSaveUser}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Modal */}
        {isCreateModalOpen && (
          <div className="admin-users-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
            <div className="admin-users-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-users-modal-header">
                <h3>Create New User</h3>
                <button onClick={() => setIsCreateModalOpen(false)}>
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="admin-users-modal-content">
                <div className="admin-users-form">
                  <div className="admin-users-form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      value={createUserData.name}
                      onChange={(e) => setCreateUserData({...createUserData, name: e.target.value})}
                      placeholder="Enter full name"
                    />
                  </div>
                  <div className="admin-users-form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={createUserData.email}
                      onChange={(e) => setCreateUserData({...createUserData, email: e.target.value})}
                      placeholder="Enter email address"
                    />
                  </div>
                  <div className="admin-users-form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={createUserData.password}
                      onChange={(e) => setCreateUserData({...createUserData, password: e.target.value})}
                      placeholder="Enter password (min 8 characters)"
                    />
                  </div>
                  <div className="admin-users-form-group">
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      value={createUserData.confirmPassword}
                      onChange={(e) => setCreateUserData({...createUserData, confirmPassword: e.target.value})}
                      placeholder="Confirm password"
                    />
                  </div>
                  <div className="admin-users-form-group">
                    <label>Role</label>
                    <Select
                      options={userRoleOptions}
                      value={userRoleOptions.find(option => option.value === createUserData.role)}
                      onChange={(option) => setCreateUserData({...createUserData, role: option?.value || ''})}
                      styles={selectStyles}
                      isSearchable={false}
                      placeholder="Select role"
                      isLoading={rolesLoading}
                    />
                  </div>
                  <div className="admin-users-form-actions">
                    <button 
                      className="admin-users-btn admin-users-btn-secondary"
                      onClick={() => setIsCreateModalOpen(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="admin-users-btn admin-users-btn-primary"
                      onClick={handleCreateNewUser}
                      disabled={isSubmitting || !createUserData.name || !createUserData.email || !createUserData.password}
                    >
                      {isSubmitting ? 'Creating...' : 'Create User'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDeleteUser}
          title="Delete User"
          message="Are you sure you want to delete this user? This action cannot be undone and will remove all associated data."
          confirmText="Delete User"
          type="danger"
          isLoading={isSubmitting}
        >
          {userToDelete && (
            <div className="admin-users-delete-modal-user">
              <div className="admin-users-delete-modal-user-avatar">
                <i className="ri-user-3-line"></i>
              </div>
              <div className="admin-users-delete-modal-user-info">
                <h4>{userToDelete.name}</h4>
                <p>{userToDelete.email}</p>
                <div className="admin-users-delete-modal-user-meta">
                  <span className="admin-users-delete-modal-user-role" 
                        style={{ color: getRoleColor(userToDelete.role?.name || 'User') }}>
                    {userToDelete.role?.name || 'User'} account
                  </span>
                  <span className="admin-users-delete-modal-user-verified">
                    {userToDelete.email_verified ? 'Verified' : 'Unverified'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </ConfirmationModal>
      </div>
    </DashboardLayout>
  );
};

export default AdminUsersManagement;
